import React from 'react';
import { useTranslation } from 'react-i18next';
import AddonServiceLines from './AddonServiceLines';
import { useReservationSummary } from './reservationSummary/useReservationSummary';
import { breakdownToServiceLines } from './reservationSummary/breakdownLines';
import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';
import DiscountEditRow from './reservationSummary/DiscountEditRow';
import AlreadyPaidSection from './reservationSummary/AlreadyPaidSection';
import type { CoveredBy } from '../utils/bookingCoverage';
import TaxBreakdown from './reservationSummary/TaxBreakdown';
import PricePreviewStateRow, { PricePreviewStatus } from './reservationSummary/PricePreviewStateRow';

// Allows surcharges (negative discount) down to -1000, in line with the venue twin.
const MIN_DISCOUNT_AMOUNT = -1000;

// Reason shown in place of a price while no apartment is selected.
const APARTMENT_IDLE_HINT = 'booking_details.reservation_summary.price_pending_apartment_selection';

interface Props {
    isCreateBooking: boolean;
    isEditBooking: boolean;
    // Persisted nights line — view mode only (booking-details responses don't
    // embed a PriceBreakdown yet). Create/edit ignore these and render `breakdown`.
    couponApplied?: number;
    // Persisted booking total — view mode only.
    total?: number;
    amountPaid?: number;
    onDiscountChange?: (value: number) => void;
    onAlreadyPaidChange?: (value: number) => void;
    // Backend price breakdown (#1203) — the single source for every price line and
    // total in create/edit mode. While it is absent a state row is shown instead
    // of the money rows; there is no FE-computed fallback.
    breakdown?: PriceBreakdown;
    // Why the breakdown is absent (idle / loading / error) — see PricePreviewStateRow.
    previewStatus?: PricePreviewStatus;
    /** Covered by an organization — replaces "Fälliger Betrag" (ADR-0008 2026-09-23). */
    coveredBy?: CoveredBy | null;
}

function ReservationSummary({
    isCreateBooking,
    isEditBooking,
    couponApplied,
    total = 0,
    amountPaid,
    onDiscountChange,
    onAlreadyPaidChange,
    breakdown,
    previewStatus = 'loading',
    coveredBy = null,
}: Props) {
    const { t } = useTranslation();

    // Every surface renders the BE breakdown (#1203) — view included. Until it
    // arrives, the money rows show a placeholder, never an FE number.
    const previewPending = !breakdown;

    const summary = useReservationSummary({
        total,
        couponApplied,
        amountPaid,
        minDiscount: MIN_DISCOUNT_AMOUNT,
        onDiscountChange,
        onAlreadyPaidChange,
        breakdown,
    });

    const editable = isCreateBooking || isEditBooking;
    const showDiscountRow = editable || couponApplied !== undefined;
    // Discount is measured against the pre-discount total (BE subtotal when present).
    const discountBound = breakdown ? breakdown.subtotal : total;

    return (
        <div className={'w-[90%] xl:w-[70%] col-span-12 md:col-span-4 border border-foreground/20 rounded py-4 px-3 mx-auto xl:ml-auto xl:mr-0 min-h-[32px]'}>
            <h2 className={'text-sm font-semibold mb-4'}>{t('booking_details.reservation_summary.price')}</h2>
            <div className={'space-y-2'}>
                {breakdown ? (
                    /* BE breakdown (#1203): every price line from one source. */
                    <AddonServiceLines lines={breakdownToServiceLines(breakdown, t)} additionalService={0} />
                ) : (
                    /* No breakdown yet (idle / loading / error) — a state row, never an FE-computed number. */
                    <PricePreviewStateRow status={previewStatus} idleHintKey={APARTMENT_IDLE_HINT} />
                )}

                {showDiscountRow && !previewPending && (
                    <DiscountEditRow summary={summary} total={discountBound} minDiscount={MIN_DISCOUNT_AMOUNT} editable={editable} />
                )}

                {!previewPending && (
                    <>
                        <div className={'border-b border-gray my-2'}></div>
                        <div>
                            <TaxBreakdown
                                displayTotal={summary.adjustedTotal}
                                taxBreakdown={breakdown?.taxBreakdown}
                                taxExemptionNote={breakdown?.taxExemptionNote}
                                taxUnresolved={breakdown?.taxUnresolved}
                                tax={summary.tax}
                                net={summary.net}
                            />

                            {amountPaid !== undefined && (
                                <AlreadyPaidSection summary={summary} amountPaid={amountPaid} editable={isEditBooking} coveredBy={coveredBy} />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ReservationSummary;
